/**
 * StageDetailsPanel - Expandable panel showing detailed stage information
 * Includes outputs, metadata, performance metrics, and logs
 */

import React, { useState } from 'react';
import { 
  X, 
  Clock, 
  DollarSign, 
  TrendingUp, 
  Activity,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Zap,
  Database,
  MessageSquare
} from 'lucide-react';
import { StageType, StageMetrics, ExecutionStatus } from '@/types/execution';
import { cn } from '@/utils/cn';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import OutputPreview from './OutputPreview';
import FeedbackPanel, { IterationSummary } from './FeedbackPanel';
import IterationHistory, { EnhancedIteration } from './IterationHistory';

interface StageDetailsPanelProps {
  stage: {
    id: StageType;
    label: string;
    description: string;
  };
  metrics: StageMetrics;
  execution: ExecutionStatus;
  onClose: () => void;
  className?: string;
}

const StageDetailsPanel: React.FC<StageDetailsPanelProps> = ({
  stage,
  metrics,
  execution,
  onClose,
  className
}) => {
  const [activeTab, setActiveTab] = useState<'outputs' | 'metrics' | 'logs' | 'iterations' | 'feedback'>('outputs');
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);
  
  // Get real-time data from the dashboard store
  const { getExecutionLogs, getExecutionProgressHistory, getExecutionCostHistory } = useDashboardServerStore();
  const logs = getExecutionLogs(execution.id);
  const progressHistory = getExecutionProgressHistory(execution.id);
  const costHistory = getExecutionCostHistory(execution.id);
  
  // Filter logs and data for this specific stage
  const stageLogs = logs.filter(log => log.stage === stage.id);
  const stageProgressHistory = progressHistory.filter(p => p.stage === stage.id);
  const stageCostUpdates = costHistory.filter(c => c.operation?.includes(stage.id));

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = () => {
    const statusConfig = {
      completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      running: { label: 'Running', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
      pending: { label: 'Pending', color: 'bg-gray-100 text-gray-800', icon: Clock },
    };

    const config = statusConfig[metrics.status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.color)}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Calculate actual stage metrics from real data
  const actualCostIncurred = stageCostUpdates.reduce((sum, cost) => sum + cost.cost_usd, 0);
  const actualDuration = stageProgressHistory.length > 0 ? 
    Math.round((new Date(stageProgressHistory[stageProgressHistory.length - 1].timestamp).getTime() - 
               new Date(stageProgressHistory[0].timestamp).getTime()) / 1000) : metrics.duration;
  const actualQualityScore = stageProgressHistory.length > 0 && stageProgressHistory[stageProgressHistory.length - 1].recent_scores.length > 0 ?
    stageProgressHistory[stageProgressHistory.length - 1].recent_scores[stageProgressHistory[stageProgressHistory.length - 1].recent_scores.length - 1] :
    metrics.quality_score;

  return (
    <div className={cn('fixed inset-0 z-50 overflow-y-auto', className)}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="space-y-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">{stage.label} Stage</h3>
                {getStatusBadge()}
              </div>
              <p className="text-sm text-gray-600">{stage.description}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50 border-b border-gray-200">
            <div className="space-y-1">
              <div className="flex items-center space-x-1 text-gray-500">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">Duration</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {formatDuration(actualDuration)}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center space-x-1 text-gray-500">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium">Cost</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                ${actualCostIncurred.toFixed(4)}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center space-x-1 text-gray-500">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Quality</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {actualQualityScore ? `${Math.round(actualQualityScore * 100)}%` : 'N/A'}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center space-x-1 text-gray-500">
                <Activity className="w-4 h-4" />
                <span className="text-xs font-medium">Progress</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {Math.round(metrics.progress * 100)}%
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between px-6">
              <nav className="flex space-x-8">
                {(['outputs', 'iterations', 'metrics', 'logs'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={cn(
                      'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
              
              {/* Feedback Button */}
              {(metrics.status === 'running' || metrics.status === 'completed') && (
                <button
                  onClick={() => setShowFeedbackPanel(true)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Provide Feedback
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {activeTab === 'outputs' && (
              <div className="space-y-6">
                <OutputPreview
                  stage={stage.id}
                  outputs={[]}
                  compact={false}
                  onViewFull={(output) => console.log('View full output:', output)}
                />
              </div>
            )}
            
            {activeTab === 'iterations' && (
              <div className="space-y-6">
                <IterationHistory
                  stage={stage.id}
                  iterations={getMockIterationHistory(stage.id, execution.id)}
                  qualityThreshold={85}
                  onIterationSelect={(iterationNum) => {
                    console.log('Selected iteration:', iterationNum);
                  }}
                />
              </div>
            )}

            {activeTab === 'metrics' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Performance Metrics */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Zap className="w-4 h-4 mr-2" />
                      Performance
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Start Time</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(Date.now() - (metrics.duration || 0) * 1000).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Processing Time</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatDuration(actualDuration)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {metrics.status}
                        </span>
                      </div>
                      {metrics.quality_score && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Quality Score</span>
                          <span className="text-sm font-medium text-gray-900">
                            {Math.round(metrics.quality_score * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resource Usage */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Database className="w-4 h-4 mr-2" />
                      Resources
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Cost Incurred</span>
                        <span className="text-sm font-medium text-gray-900">
                          ${actualCostIncurred.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Error Count</span>
                        <span className="text-sm font-medium text-gray-900">
                          {metrics.errors}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Iterations</span>
                        <span className="text-sm font-medium text-gray-900">
                          1
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Model Used</span>
                        <span className="text-sm font-medium text-gray-900">
                          GPT-4o
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {metrics.status === 'running' && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-900">Progress</span>
                      <span className="text-gray-600">{Math.round(metrics.progress * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${metrics.progress * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'logs' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Stage Logs</h4>
                {stageLogs.length > 0 ? (
                  <div className="space-y-2">
                    {stageLogs.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className={cn(
                          'w-2 h-2 rounded-full mt-2 flex-shrink-0',
                          log.level === 'error' ? 'bg-red-400' :
                          log.level === 'success' ? 'bg-green-400' :
                          log.level === 'warning' ? 'bg-yellow-400' : 'bg-blue-400'
                        )} />
                        <div className="flex-1 space-y-1">
                          <div className="text-sm text-gray-900">{log.message}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleString()}
                          </div>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded mt-1">
                              {JSON.stringify(log.details, null, 2)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-500 text-sm">No logs available for this stage</div>
                    {metrics.status === 'pending' && (
                      <div className="text-xs text-gray-400 mt-1">Logs will appear when stage execution begins</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Feedback Panel Overlay */}
        {showFeedbackPanel && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <FeedbackPanel
                execution={execution}
                stage={stage.id}
                currentIteration={getMockCurrentIteration(stage.id, execution.id)}
                onFeedbackSubmit={(feedback) => {
                  console.log('Feedback submitted:', feedback);
                  // Store in localStorage for now
                  const existingFeedback = JSON.parse(localStorage.getItem('user-feedback') || '[]');
                  existingFeedback.push(feedback);
                  localStorage.setItem('user-feedback', JSON.stringify(existingFeedback));
                  setShowFeedbackPanel(false);
                }}
                onClose={() => setShowFeedbackPanel(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Mock data helpers (will be replaced with real data from API/store)
const getMockCurrentIteration = (stage: StageType, _executionId: string): IterationSummary => {
  return {
    iteration_number: 2,
    scores: {
      hook_effectiveness: 78,
      narrative_clarity: 85,
      visual_coherence: 82,
      persona_consistency: 90,
      anti_cliche_score: 88,
      overall: 84
    },
    decision: 'improve',
    ai_feedback: 'Good narrative structure, but the hook could be more specific to the target audience. The visual metaphors are strong.',
    persona_used: stage === 'script' ? 'The Writer' : stage === 'vision' ? 'The Architect' : 'The Operator',
    is_best_attempt: false,
    what_worked: ['Strong narrative structure', 'Effective visual metaphors', 'Clear transformation arc'],
    what_failed: ['Hook specificity', 'Audience targeting'],
    timestamp: new Date().toISOString()
  };
};

const getMockIterationHistory = (stage: StageType, _executionId: string): EnhancedIteration[] => {
  const now = Date.now();
  return [
    {
      iteration_number: 1,
      trigger: 'ai_evaluation',
      decision: 'improve',
      feedback_source: {
        type: 'gpt5',
        ai_evaluation: {
          feedback: 'Initial output has good structure but lacks specificity in the hook and needs stronger visual metaphors.',
          decision: 'improve',
          confidence: 0.85
        }
      },
      scores: {
        hook_effectiveness: 65,
        narrative_clarity: 80,
        visual_coherence: 75,
        persona_consistency: 85,
        anti_cliche_score: 82,
        overall: 77
      },
      improvements: ['Enhanced hook specificity', 'Strengthened visual metaphors'],
      preserved_from_previous: ['Narrative structure', 'Transformation arc'],
      persona_used: stage === 'script' ? 'The Writer' : stage === 'vision' ? 'The Architect' : 'The Operator',
      timestamp: new Date(now - 300000).toISOString(), // 5 minutes ago
      is_best_attempt: false,
      processing_time_seconds: 45,
      cost_usd: 0.0234
    },
    {
      iteration_number: 2,
      trigger: 'ai_evaluation',
      decision: 'improve',
      feedback_source: {
        type: 'gpt5',
        ai_evaluation: {
          feedback: 'Better hook specificity achieved, but still needs more audience targeting. Visual coherence improved.',
          decision: 'improve',
          confidence: 0.78
        }
      },
      scores: {
        hook_effectiveness: 78,
        narrative_clarity: 85,
        visual_coherence: 82,
        persona_consistency: 90,
        anti_cliche_score: 88,
        overall: 84
      },
      improvements: ['Improved audience targeting', 'Enhanced visual coherence'],
      preserved_from_previous: ['Hook structure', 'Visual metaphors', 'Transformation arc'],
      persona_used: stage === 'script' ? 'The Writer' : stage === 'vision' ? 'The Architect' : 'The Operator',
      timestamp: new Date(now - 120000).toISOString(), // 2 minutes ago
      is_best_attempt: true,
      processing_time_seconds: 38,
      cost_usd: 0.0198
    }
  ];
};

export default StageDetailsPanel;